// Extend DateAdapter logic for weekday labels.
import { DateAdapter } from '@angular/material/core';
import { DateTime } from 'luxon';

export const TWO_LETTER_WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export class CustomDatepickerAdapter extends DateAdapter<DateTime> {
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    // Return the two-letter weekday labels
    return TWO_LETTER_WEEKDAYS;
  }

  addCalendarDays(date: DateTime, days: number): DateTime {
    return date.plus({ days: days });
  }

  addCalendarMonths(date: DateTime, months: number): DateTime {
    return date.plus({ months: months });
  }

  addCalendarYears(date: DateTime, years: number): DateTime {
    return date.plus({ years: years });
  }

  clone(date: DateTime): DateTime {
    return date;
  }

  createDate(year: number, month: number, date: number): DateTime {
    const result = DateTime.local(year, month + 1, date); // Luxon's months are 1-based
    if (!result.isValid) {
      throw new Error(`Invalid date "${date}" for year "${year}" and month "${month}".`);
    }
    return result;
  }

  format(date: DateTime, displayFormat: any): string {
    return date.toFormat(displayFormat);
  }

  getDate(date: DateTime): number {
    return date.day;
  }

  getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => `${i + 1}`);
  }

  getDayOfWeek(date: DateTime): number {
    return date.weekday === 7 ? 0 : date.weekday; // Convert Luxon's weekday to 0-based (Sun = 0)
  }

  getFirstDayOfWeek(): number {
    return 7; // Sunday is the first day of the week for us.
  }

  getMonth(date: DateTime): number {
    return date.month - 1; // Convert 1-based Luxon month to 0-based
  }

  getMonthNames(style: "long" | "short" | "narrow"): string[] {
    const names: string[] = [];
    for (let i = 0; i < 12; i++) {
      const date = DateTime.now().set({ month: i + 1 });
      names.push(date.monthLong ?? '');
    }
    return names;
  }

  getNumDaysInMonth(date: DateTime): number {
    return date.daysInMonth ?? 30;
  }

  getYear(date: DateTime): number {
    return date.year;
  }

  getYearName(date: DateTime): string {
    return date.toFormat('yyyy');
  }

  invalid(): DateTime {
    return DateTime.now();
  }

  isDateInstance(obj: any): boolean {
    return DateTime.isDateTime(obj);
  }

  isValid(date: DateTime): boolean {
    return date.isValid;
  }

  parse(value: any, parseFormat: any): DateTime | null {
    return DateTime.fromFormat(value, parseFormat);
  }

  toIso8601(date: DateTime): string {
    return date.toISO()?.toString() ?? '';
  }

  today(): DateTime {
    return DateTime.now();
  }
}
